import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { endOfYear, startOfYear } from 'date-fns'

import BaseModal from 'components/Modals/BaseModal'
import { DateTimePicker } from 'components/UI'
import { Button as MerinioButton, Card, Input } from 'ui'
import { getUserName } from 'services/Utilities'
import { Format } from 'services/Dates'
import { createErrorNotification } from 'store/actions/notifications'
import Api from 'services/Api'

import { t, currentLang } from 'translations'

const EmployeeRequestsSummaryModal = ({ userId }) => {
  const dispatch = useDispatch()
  const { timeOffTypes, attendanceTypes } = useSelector(state => state.resources)
  const [timeOffRequests, setTimeOffRequests] = useState([])
  const [attendanceRequests, setAttendanceRequests] = useState([])
  const [shiftsAssignedTo, setShiftsAssignedTo] = useState([])
  const [[rangeStart, rangeEnd], setDates] = useState([
    Format(startOfYear(new Date()), { outFormat: 'date' }),
    Format(endOfYear(new Date()), { outFormat: 'date' })
  ])

  useEffect(() => {
    // Function to fetch the time off of the users for the current year
    async function fetchUserRequests() {
      const [{ vacations, success: success1 }, { result: leaves, success: success2 }, { result: shifts, success: success3 }] = await Promise.all([
        Api(`/requests/vacation`, { params: { userId, rangeStart, rangeEnd } }),
        Api(`/requests/leave`, { params: { userId, rangeStart, rangeEnd } }),
        Api('/schedule/schedule', { params: { employeeId: userId, rangeStart, rangeEnd, useNewReturn: true } })
      ])
      if (!success1 || !success2 || !success3) return dispatch(createErrorNotification())
      setTimeOffRequests(vacations)
      setAttendanceRequests(leaves)
      setShiftsAssignedTo(shifts)
    }
    fetchUserRequests()
  }, [dispatch, rangeEnd, rangeStart, userId])

  const datePickerOptions = { wrap: true, mode: 'range', dateFormat: 'Y-m-d' }

  return (
    <BaseModal title={`components.employeeRequestsSummary`} titleVar={{ name: getUserName(userId) }}>
      <>
        <div className="mt-4">
          <DateTimePicker
            value={[rangeStart, rangeEnd]}
            onChange={([rangeStart, rangeEnd]) =>
              rangeStart && rangeEnd ? setDates([Format(rangeStart, { outFormat: 'date' }), Format(rangeEnd), { outFormat: 'date' }]) : null
            }
            options={datePickerOptions}
          >
            {' '}
            <div className="input-group">
              <Input type="text" placeholder={t('searchBar.SELECT_DATES')} data-input />
              <div className="input-group-append" id="button-addon4">
                <MerinioButton type="button" color="secondary" data-toggle outlined>
                  <i className="fas fa-calendar-alt"></i>
                </MerinioButton>
                <MerinioButton type="button" color="secondary" data-clear outlined>
                  <i className="fas fa-trash"></i>
                </MerinioButton>
              </div>
            </div>
          </DateTimePicker>
        </div>
        <div className="mt-4">
          {/* Shifts the user is assigned to*/}
          <b>{t(`components.employeeRequestsSummary.SHIFTS`)}</b>
          <Card maxHeight="10rem" className="bg-light pt-3 my-2">
            <ul>
              {shiftsAssignedTo.length ? (
                <li>{t(`components.employeeRequestsSummary.SHIFTSASSIGNEDTO`, { count: shiftsAssignedTo.length })}</li>
              ) : (
                t(`components.employeeRequestsSummary.NOSHIFTSASSIGNEDTO`)
              )}
            </ul>
          </Card>
          {/* Attendence requests*/}
          <b>{t(`components.employeeRequestsSummary.ATTENDANCEREQUESTS`)}</b>
          <Card maxHeight="10rem" className="bg-light pt-3 my-2">
            <ul>
              {attendanceRequests.length
                ? attendanceTypes.map(type => {
                    const count = attendanceRequests.filter(({ requestTypeId }) => requestTypeId === type.id).length
                    const typeName = t(`employee.attendanceForm.${type.name}`)
                    if (!count) return null
                    return <li key={type.id}>{t(`components.employeeRequestsSummary.REQUESTSBYTYPE`, { count, typeName })}</li>
                  })
                : t(`components.employeeRequestsSummary.NOREQUESTS`)}
            </ul>
          </Card>
          {/* Time off requests*/}
          <b>{t(`components.employeeRequestsSummary.TIMEOFFSREQUESTS`)}</b>
          <Card maxHeight="10rem" className="bg-light pt-3 my-2">
            <ul>
              {timeOffRequests.length
                ? timeOffTypes.map(type => {
                    const count = timeOffRequests.filter(({ vacationTypeId }) => vacationTypeId === type._id).length
                    const typeName = type[`name${currentLang(true)}`]
                    if (!count) return null
                    return <li key={type.id}>{t(`components.employeeRequestsSummary.REQUESTSBYTYPE`, { count, typeName })}</li>
                  })
                : t(`components.employeeRequestsSummary.NOREQUESTS`)}
            </ul>
          </Card>
        </div>
      </>
    </BaseModal>
  )
}

export default EmployeeRequestsSummaryModal
